import { firestoreServerMillis } from '../serverClock'
import type { PrintJobServerTime } from '../../types'

/** Document field written with `serverTimestamp()` on every order/bill persist. */
export const LIVE_UPDATED_AT_FIELD = 'updatedAt'

/** Snapshot row tagged with the document's server `updatedAt` (not device clocks). */
export interface VersionedRow<T> {
  id: string
  data: T
  /** Millis from `updatedAt`, or 0 when the field is missing (legacy docs). */
  serverUpdatedAtMs: number
}

/** Server commit millis from the `updatedAt` field. Never use snapshot readTime or Date.now(). */
export function fieldServerUpdatedAtMs(value: unknown): number {
  if (value == null) return 0
  const fromStamp = firestoreServerMillis(value as PrintJobServerTime)
  if (fromStamp != null) return fromStamp
  if (value instanceof Date) {
    const ms = value.getTime()
    return Number.isFinite(ms) ? ms : 0
  }
  if (typeof value === 'string') {
    const ms = Date.parse(value)
    return Number.isFinite(ms) ? ms : 0
  }
  return 0
}

export function versionedRowFromSnap<T>(
  d: {
    id: string
    data: (options?: { serverTimestamps?: 'estimate' | 'previous' }) => object
  },
  toData: (id: string, fields: Record<string, unknown>) => T,
): VersionedRow<T> {
  const raw = { ...(d.data({ serverTimestamps: 'estimate' }) as Record<string, unknown>) }
  const stamp = raw[LIVE_UPDATED_AT_FIELD]
  delete raw[LIVE_UPDATED_AT_FIELD]
  return {
    id: d.id,
    data: toData(d.id, raw),
    serverUpdatedAtMs: fieldServerUpdatedAtMs(stamp),
  }
}

/**
 * Last-id merge that keeps the copy with the greater server `updatedAt`.
 * Ties keep the first row (same commit). Missing time (0) loses to a dated copy.
 */
export function mergeByServerUpdateTime<T>(
  ...lists: ReadonlyArray<ReadonlyArray<VersionedRow<T>>>
): T[] {
  const byId = new Map<string, VersionedRow<T>>()
  for (const list of lists) {
    for (const row of list) {
      const existing = byId.get(row.id)
      if (!existing || row.serverUpdatedAtMs > existing.serverUpdatedAtMs) {
        byId.set(row.id, row)
      }
    }
  }
  return [...byId.values()].map((row) => row.data)
}
